import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ANALYTICS_ENABLED,
  ANALYTICS_PUBLISH_INTERVAL_MS,
} from "utils/constants";
import { ClientHints, ErrorKind, ViewKind } from "types/analytics";
import { MqttClientContext } from "services/mqtt-client";
import { Topics } from "services/topics";
import { useStore } from "hooks/use-store";
import { requestClientHints } from "services/analytics/client-hints";
import { buildDeviceProfile } from "services/analytics/device-profile";
import { startErrorTracking } from "services/analytics/error-tracker";
import { startPressTracking } from "services/analytics/press-tracker";
import { UsageBuffer } from "services/analytics/usage-buffer";

/**
 * Collects plotter/client analytics and publishes them over MQTT for the
 * embedded device to store.
 *
 * Two publishes happen here:
 *  - `helm_analytics/profile`, retained, once per session: what this plotter is
 *    and which web platform features it supports.
 *  - `helm_analytics/usage`, every minute: in-memory counters for screens and
 *    widgets viewed, session length, control presses and client errors.
 *
 * All buffering is in memory — neither localStorage nor sessionStorage is used —
 * so a lost connection simply means the affected window is never delivered.
 */

export interface AnalyticsApi {
  /** Marks a view as entered; call the returned function when it is left */
  trackView: (kind: ViewKind, name: string) => () => void;
  /**
   * Counts a named event. Control presses are already captured automatically by
   * the press tracker — use this for things a press cannot express, such as a
   * mode actually changing or a long-running action completing.
   */
  trackEvent: (name: string) => void;
  /** Reports a handled error that would otherwise go unseen */
  trackError: (error: unknown, context?: string) => void;
}

const noop = () => undefined;

const disabledApi: AnalyticsApi = {
  trackView: () => noop,
  trackEvent: noop,
  trackError: noop,
};

export const AnalyticsContext = createContext<AnalyticsApi>(disabledApi);

export const AnalyticsProvider = ({ children }: { children: any }) => {
  const client = useContext(MqttClientContext);

  const screen = useStore((state) => state.screen);
  const tripId = useStore((state) => state.tripId);
  const browserSpeed = useStore((state) => state.browserSpeed);
  const deviceId = useStore((state) => state.config?.device_id);
  const vesselId = useStore((state) => state.config?.vessel_id);

  // Client hints resolve asynchronously; null means "not resolved yet"
  const [clientHints, setClientHints] = useState<ClientHints | null>(null);

  const bufferRef = useRef(new UsageBuffer());
  const tripIdRef = useRef(tripId);
  // Config can arrive after the first publishes, so read it through a ref
  // rather than capturing whatever it was when the callback was memoised
  const deviceIdRef = useRef(deviceId);
  const vesselIdRef = useRef(vesselId);
  const profilePublishedRef = useRef(false);

  tripIdRef.current = tripId;
  deviceIdRef.current = deviceId;
  vesselIdRef.current = vesselId;

  const publish = useCallback(
    (topic: Topics, payload: any, retain: boolean) => {
      if (!client?.connected) return;

      try {
        client.publish(topic, JSON.stringify(payload), { qos: 0, retain });
      } catch (error) {
        // Analytics must never take the app down with it
        console.error("Analytics publish failed:", error);
      }
    },
    [client]
  );

  const flush = useCallback(
    (final = false) => {
      const buffer = bufferRef.current;
      if (!final && !buffer.hasData()) return;

      publish(
        Topics.AnalyticsUsage,
        buffer.drain(
          tripIdRef.current || null,
          deviceIdRef.current ?? null,
          vesselIdRef.current ?? null,
          final
        ),
        false
      );
    },
    [publish]
  );

  // Memoised so consumers can safely use these in effect dependency arrays —
  // a new identity per render would re-open every tracked view on each render
  const api = useMemo<AnalyticsApi>(
    () => ({
      trackView: (kind: ViewKind, name: string) => {
        const handle = bufferRef.current.beginView(kind, name);
        return () => bufferRef.current.endView(handle);
      },

      trackEvent: (name: string) => {
        bufferRef.current.recordEvent(name);
      },

      trackError: (error: unknown, context?: string) => {
        const anyError = error as any;
        const message = anyError?.message ?? String(error);

        bufferRef.current.recordError({
          kind: ErrorKind.Reported,
          message: context ? `${context}: ${message}` : message,
          stack: anyError?.stack ?? null,
        });
      },
    }),
    []
  );

  // Global press and error listeners, installed once for the session
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;

    const stopPresses = startPressTracking(bufferRef.current);
    const stopErrors = startErrorTracking(bufferRef.current);

    return () => {
      stopPresses();
      stopErrors();
    };
  }, []);

  // User-Agent Client Hints, which give the true Chromium version and hardware
  // model where they exist. Unavailable outside a secure context, so on the units
  // this resolves to `available: false` and the user agent stays the source.
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;

    let cancelled = false;
    requestClientHints().then((hints) => {
      if (!cancelled) setClientHints(hints);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Publish the device profile once connected. Browser speed and client hints
  // both land asynchronously, so the profile is republished as they arrive — the
  // topic is retained, so the most complete profile is the one that sticks.
  useEffect(() => {
    if (!ANALYTICS_ENABLED || !client?.connected) return;
    if (profilePublishedRef.current) return;

    publish(
      Topics.AnalyticsProfile,
      buildDeviceProfile(
        client,
        browserSpeed,
        clientHints,
        deviceId ?? null,
        vesselId ?? null
      ),
      true
    );

    if (browserSpeed !== null && clientHints !== null && deviceId && vesselId) {
      profilePublishedRef.current = true;
    }
  }, [
    client,
    client?.connected,
    browserSpeed,
    clientHints,
    deviceId,
    vesselId,
    publish,
  ]);

  // Publish the accumulated usage counters on an interval, and once more while
  // the page unloads so clean exits are distinguishable from crashes
  useEffect(() => {
    if (!ANALYTICS_ENABLED || !client?.connected) return;

    const interval = setInterval(flush, ANALYTICS_PUBLISH_INTERVAL_MS);

    const onPageHide = () => flush(true);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [client, client?.connected, flush]);

  // Screen views come straight from the store, so no screen needs to opt in
  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;

    const handle = bufferRef.current.beginView(ViewKind.Screen, screen);
    return () => bufferRef.current.endView(handle);
  }, [screen]);

  return (
    <AnalyticsContext.Provider value={ANALYTICS_ENABLED ? api : disabledApi}>
      {children}
    </AnalyticsContext.Provider>
  );
};
